import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import MilestoneDocumentModal from '@/components/MilestoneDocumentModal';
import { 
  Clock, Play, Pause, CheckCircle, AlertCircle, 
  FileText, ClipboardCheck, Send, Package, Settings,
  Users, Calendar, TrendingUp, DollarSign, FolderKanban, Eye, MessageSquare
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WorkBoard = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeProjects, setActiveProjects] = useState([]);
  const [todayStats, setTodayStats] = useState({
    active_projects: 0,
    hours_today: 0,
    tasks_completed: 0,
    pending_tasks: 0
  });
  
  // Time Tracker State
  const [activeTimer, setActiveTimer] = useState(null);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [showTimeLogModal, setShowTimeLogModal] = useState(false);
  const [selectedProjectForTime, setSelectedProjectForTime] = useState(null);
  const [manualTimeLog, setManualTimeLog] = useState({
    project_id: '',
    milestone_id: '',
    hours: 0,
    description: '',
    date: new Date().toISOString().split('T')[0]
  });
  
  // Memo State
  const [selectedProject, setSelectedProject] = useState(null);
  const [showMemoModal, setShowMemoModal] = useState(false);
  const [projectMemos, setProjectMemos] = useState([]);
  const [newMemo, setNewMemo] = useState({
    memo_type: 'note',
    priority: 'normal',
    subject: '',
    message: ''
  });
  
  // Document Modal State
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [documentType, setDocumentType] = useState(null);

  useEffect(() => {
    loadWorkBoardData();
    loadActiveTimer();
  }, []);

  // Timer effect
  useEffect(() => {
    let interval;
    if (activeTimer) {
      interval = setInterval(() => {
        setTimerSeconds(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [activeTimer]);

  const loadWorkBoardData = async () => {
    setLoading(true);
    try {
      const [projectsRes, statsRes] = await Promise.all([
        axios.get(`${API}/workboard/projects`),
        axios.get(`${API}/workboard/stats/today`)
      ]);
      
      const projects = projectsRes.data;
      
      // Fetch variance data for projects with quotes
      const projectsWithVariance = await Promise.all(
        projects.map(async (project) => {
          if (project.quote_id) {
            try {
              const varianceRes = await axios.get(`${API}/quotes/${project.quote_id}/variance`);
              return { ...project, variance: varianceRes.data };
            } catch (error) {
              return project;
            }
          }
          return project;
        })
      );
      
      setActiveProjects(projectsWithVariance);
      setTodayStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load workboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadActiveTimer = async () => {
    try {
      const response = await axios.get(`${API}/time-tracker/active`);
      if (response.data.active_timer) {
        setActiveTimer(response.data.active_timer);
        const elapsed = Math.floor((Date.now() - new Date(response.data.active_timer.start_time)) / 1000);
        setTimerSeconds(elapsed);
      }
    } catch (error) {
      console.log('No active timer');
    }
  };

  const startTimer = async (projectId, milestoneId) => {
    try {
      const response = await axios.post(`${API}/time-tracker/start`, {
        project_id: projectId,
        milestone_id: milestoneId,
        employee_id: user.id
      });
      setActiveTimer(response.data);
      setTimerSeconds(0);
      toast.success('Timer started');
    } catch (error) {
      toast.error('Failed to start timer');
    }
  };

  const stopTimer = async () => {
    try {
      await axios.post(`${API}/time-tracker/stop`, {
        timer_id: activeTimer.id
      });
      setActiveTimer(null);
      setTimerSeconds(0);
      toast.success('Time logged successfully');
      loadWorkBoardData();
    } catch (error) {
      toast.error('Failed to stop timer');
    }
  };

  const openTimeLogModal = (project = null) => {
    if (project) {
      setSelectedProjectForTime(project);
      setManualTimeLog({
        project_id: project.id,
        milestone_id: project.milestones?.[0]?.id || '',
        hours: 0,
        description: '',
        date: new Date().toISOString().split('T')[0]
      });
    }
    setShowTimeLogModal(true);
  };

  const logManualTime = async () => {
    try {
      await axios.post(`${API}/time-tracker/manual`, manualTimeLog);
      toast.success('Time logged manually');
      setShowTimeLogModal(false);
      setSelectedProjectForTime(null);
      setManualTimeLog({
        project_id: '',
        milestone_id: '',
        hours: 0,
        description: '',
        date: new Date().toISOString().split('T')[0]
      });
      loadWorkBoardData();
    } catch (error) {
      toast.error('Failed to log time');
    }
  };

  const openMemos = async (project) => {
    setSelectedProject(project);
    setShowMemoModal(true);
    try {
      const response = await axios.get(`${API}/projects/${project.id}/memos`);
      setProjectMemos(response.data.memos);
    } catch (error) {
      toast.error('Failed to load memos');
    }
  };

  const createMemo = async () => {
    if (!newMemo.subject || !newMemo.message) {
      toast.error('Subject and message are required');
      return;
    }
    
    try {
      await axios.post(`${API}/projects/${selectedProject.id}/memos`, newMemo);
      toast.success('Memo added');
      setNewMemo({
        memo_type: 'note',
        priority: 'normal',
        subject: '',
        message: ''
      });
      openMemos(selectedProject); // Reload memos
    } catch (error) {
      toast.error('Failed to create memo');
    }
  };

  const openDocumentModal = (project, milestone, docType) => {
    setSelectedProject(project);
    setSelectedMilestone(milestone);
    setDocumentType(docType);
    setShowDocumentModal(true);
  };

  const handleDocumentCreated = (document) => {
    toast.success('Document created successfully');
    loadWorkBoardData(); // Reload to show updated counts
  };

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusColor = (status) => {
    const colors = {
      'active': 'bg-green-500',
      'pending': 'bg-yellow-500',
      'completed': 'bg-blue-500',
      'on_hold': 'bg-red-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Clock className="animate-spin" size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-black uppercase tracking-tight">WorkBoard</h1>
        <p className="text-muted-foreground mt-2">Project tracking, time logging, and daily operations</p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-mono uppercase text-muted-foreground">Active Projects</p>
                <p className="text-3xl font-black mt-2">{todayStats.active_projects}</p>
              </div>
              <FolderKanban size={32} className="text-primary" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-mono uppercase text-muted-foreground">Hours Today</p>
                <p className="text-3xl font-black mt-2">{todayStats.hours_today.toFixed(1)}</p>
              </div>
              <Clock size={32} className="text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-mono uppercase text-muted-foreground">Completed</p>
                <p className="text-3xl font-black mt-2">{todayStats.tasks_completed}</p>
              </div>
              <CheckCircle size={32} className="text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-mono uppercase text-muted-foreground">Pending</p>
                <p className="text-3xl font-black mt-2">{todayStats.pending_tasks}</p>
              </div>
              <AlertCircle size={32} className="text-yellow-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Active Time Tracker */}
      {activeTimer && (
        <Card className="border-primary/50 bg-gradient-to-r from-primary/10 to-blue-500/10">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                <div>
                  <p className="text-sm font-mono uppercase text-muted-foreground">Active Timer</p>
                  <p className="font-bold">{activeTimer.project_name} - {activeTimer.milestone_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <p className="text-4xl font-black font-mono">{formatTime(timerSeconds)}</p>
                <Button onClick={stopTimer} variant="destructive" size="lg">
                  <Pause className="mr-2" size={18} />
                  Stop Timer
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl font-bold uppercase">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <Button 
              variant="outline" 
              className="h-24 flex-col gap-2"
              onClick={() => openTimeLogModal()}
            >
              <Clock size={24} />
              <span className="text-xs font-mono uppercase">Log Time</span>
            </Button>
            
            <Button variant="outline" className="h-24 flex-col gap-2">
              <FileText size={24} />
              <span className="text-xs font-mono uppercase">QC Sheets</span>
            </Button>
            
            <Button variant="outline" className="h-24 flex-col gap-2">
              <ClipboardCheck size={24} />
              <span className="text-xs font-mono uppercase">Checklists</span>
            </Button>
            
            <Button variant="outline" className="h-24 flex-col gap-2">
              <Send size={24} />
              <span className="text-xs font-mono uppercase">Send RFQ</span>
            </Button>
            
            <Button variant="outline" className="h-24 flex-col gap-2">
              <Package size={24} />
              <span className="text-xs font-mono uppercase">Inventory</span>
            </Button>
            
            <Button variant="outline" className="h-24 flex-col gap-2">
              <Settings size={24} />
              <span className="text-xs font-mono uppercase">Decisions</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Active Projects */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl font-bold uppercase">Active Projects</CardTitle>
        </CardHeader>
        <CardContent>
          {activeProjects.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">No active projects</p>
          ) : (
            <div className="space-y-4">
              {activeProjects.map(project => (
                <Card key={project.id} className="border-border">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-bold">{project.project_name}</h3>
                          <Badge className={getStatusColor(project.status)}>
                            {project.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Customer: {project.customer_name}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Assigned: {project.assigned_employees?.join(', ') || 'Unassigned'}
                        </p>
                      </div>
                      
                      <div className="text-right">
                        <p className="text-sm font-mono text-muted-foreground">Hours</p>
                        <p className="text-2xl font-black">
                          {project.hours_spent} / {project.hours_estimated}
                        </p>
                        <div className="w-32 bg-border rounded-full h-2 mt-2">
                          <div 
                            className="bg-primary rounded-full h-2"
                            style={{ 
                              width: `${Math.min((project.hours_spent / project.hours_estimated) * 100, 100)}%` 
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Budget Variance */}
                    {project.variance?.has_project && (
                      <div className="p-3 border border-border rounded-sm mb-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-mono uppercase text-muted-foreground">Budget Variance</p>
                            <p className="text-lg font-bold">
                              ${Math.abs(project.variance.summary.total_variance).toFixed(2)}
                            </p>
                          </div>
                          <div className="text-right">
                            <Badge 
                              variant={
                                project.variance.summary.status === 'over_budget' ? 'destructive' :
                                project.variance.summary.status === 'under_budget' ? 'default' :
                                'secondary'
                              }
                              className="text-xs font-mono"
                            >
                              {project.variance.summary.status === 'over_budget' ? '📉 OVER' :
                               project.variance.summary.status === 'under_budget' ? '📈 UNDER' :
                               '✓ ON BUDGET'}
                            </Badge>
                            <p className="text-sm font-mono mt-1">
                              {project.variance.summary.variance_percentage > 0 ? '+' : ''}
                              {project.variance.summary.variance_percentage.toFixed(1)}%
                            </p>
                          </div>
                        </div>
                        <div className="mt-2 pt-2 border-t border-border text-xs">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Quoted:</span>
                            <span className="font-mono">${project.variance.summary.total_quoted.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Actual:</span>
                            <span className="font-mono">${project.variance.summary.total_actual.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Milestones */}
                    <div className="space-y-2 mb-4">
                      <p className="text-xs font-mono uppercase text-muted-foreground">Milestones</p>
                      {project.milestones?.map(milestone => (
                        <div 
                          key={milestone.id} 
                          className="flex items-center justify-between p-3 border border-border rounded-sm"
                        >
                          <div className="flex items-center gap-3">
                            <input 
                              type="checkbox" 
                              checked={milestone.completed}
                              className="w-5 h-5"
                              readOnly
                            />
                            <span className="text-sm">{milestone.description}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-xs font-mono text-muted-foreground">
                              {milestone.hours_spent}h / {milestone.hours_estimated}h
                            </span>
                            {!activeTimer && (
                              <Button 
                                size="sm" 
                                onClick={() => startTimer(project.id, milestone.id)}
                              >
                                <Play size={14} className="mr-1" />
                                Start
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Quick Actions Grid */}
                    <div className="mt-4 pt-4 border-t border-border">
                      <p className="text-xs font-mono uppercase text-muted-foreground mb-3">Quick Actions</p>
                      <div className="grid grid-cols-4 gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => navigate(`/workboard/projects/${project.id}`)}
                          className="flex-col h-16 gap-1"
                        >
                          <Eye size={16} />
                          <span className="text-xs">View</span>
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openTimeLogModal(project)}
                          className="flex-col h-16 gap-1"
                        >
                          <Clock size={16} />
                          <span className="text-xs">Log Time</span>
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openMemos(project)}
                          className="flex-col h-16 gap-1"
                        >
                          <MessageSquare size={16} />
                          <span className="text-xs">Memos</span>
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="flex-col h-16 gap-1"
                          onClick={() => {
                            if (project.milestones && project.milestones.length > 0) {
                              openDocumentModal(project, project.milestones[0], 'qc_sheet');
                            } else {
                              toast.error('No milestones found for this project');
                            }
                          }}
                        >
                          <FileText size={16} />
                          <span className="text-xs">QC Sheets</span>
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="flex-col h-16 gap-1"
                          onClick={() => {
                            if (project.milestones && project.milestones.length > 0) {
                              openDocumentModal(project, project.milestones[0], 'checklist');
                            } else {
                              toast.error('No milestones found for this project');
                            }
                          }}
                        >
                          <ClipboardCheck size={16} />
                          <span className="text-xs">Checklists</span>
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="flex-col h-16 gap-1"
                        >
                          <Send size={16} />
                          <span className="text-xs">Send RFQ</span>
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="flex-col h-16 gap-1"
                          onClick={() => {
                            if (project.milestones && project.milestones.length > 0) {
                              openDocumentModal(project, project.milestones[0], 'support');
                            } else {
                              toast.error('No milestones found for this project');
                            }
                          }}
                        >
                          <Package size={16} />
                          <span className="text-xs">Support Docs</span>
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="flex-col h-16 gap-1"
                          onClick={() => {
                            if (project.milestones && project.milestones.length > 0) {
                              openDocumentModal(project, project.milestones[0], 'decision_planning');
                            } else {
                              toast.error('No milestones found for this project');
                            }
                          }}
                        >
                          <Settings size={16} />
                          <span className="text-xs">Decisions</span>
                        </Button>
                      </div>
                    </div>

                    {/* Due Date */}
                    {project.due_date && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mt-3">
                        <Calendar size={16} />
                        <span>Due: {new Date(project.due_date).toLocaleDateString()}</span>
                        <span className="text-yellow-500">({project.days_remaining} days left)</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manual Time Log Modal */}
      {showTimeLogModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full">
            <CardHeader>
              <CardTitle>Log Time Manually</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium">Project</label>
                <select 
                  className="w-full mt-1 px-3 py-2 border rounded-sm bg-background"
                  value={manualTimeLog.project_id}
                  onChange={(e) => {
                    const project = activeProjects.find(p => p.id === e.target.value);
                    setSelectedProjectForTime(project);
                    setManualTimeLog({
                      ...manualTimeLog, 
                      project_id: e.target.value,
                      milestone_id: project?.milestones?.[0]?.id || ''
                    });
                  }}
                >
                  <option value="">Select Project</option>
                  {activeProjects.map(p => (
                    <option key={p.id} value={p.id}>{p.project_name}</option>
                  ))}
                </select>
              </div>

              {selectedProjectForTime && selectedProjectForTime.milestones?.length > 0 && (
                <div>
                  <label className="text-sm font-medium">Milestone</label>
                  <select 
                    className="w-full mt-1 px-3 py-2 border rounded-sm bg-background"
                    value={manualTimeLog.milestone_id}
                    onChange={(e) => setManualTimeLog({...manualTimeLog, milestone_id: e.target.value})}
                  >
                    <option value="">Select Milestone</option>
                    {selectedProjectForTime.milestones.map(m => (
                      <option key={m.id} value={m.id}>{m.description}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="text-sm font-medium">Hours</label>
                <Input 
                  type="number" 
                  step="0.5"
                  value={manualTimeLog.hours}
                  onChange={(e) => setManualTimeLog({...manualTimeLog, hours: parseFloat(e.target.value)})}
                />
              </div>

              <div>
                <label className="text-sm font-medium">Description</label>
                <Input 
                  value={manualTimeLog.description}
                  onChange={(e) => setManualTimeLog({...manualTimeLog, description: e.target.value})}
                  placeholder="What did you work on?"
                />
              </div>

              <div>
                <label className="text-sm font-medium">Date</label>
                <Input 
                  type="date"
                  value={manualTimeLog.date}
                  onChange={(e) => setManualTimeLog({...manualTimeLog, date: e.target.value})}
                />
              </div>

              <div className="flex gap-3">
                <Button onClick={logManualTime} className="flex-1">
                  Log Time
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowTimeLogModal(false);
                    setSelectedProjectForTime(null);
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      {/* Milestone Document Modal */}
      <MilestoneDocumentModal
        isOpen={showDocumentModal}
        onClose={() => setShowDocumentModal(false)}
        projectId={selectedProject?.id}
        milestone={selectedMilestone}
        documentType={documentType}
        onDocumentCreated={handleDocumentCreated}
      />
    </div>
  );
};

export default WorkBoard;
